import { memo } from 'react';

import { Button } from '@air/primitive-button';

import { type AnnouncementAction, isAnnouncementCommandAction } from '../constants';
import { getAnnouncementAnchorTargetProps, useAnnouncementActionClick } from './useAnnouncementActionClick';

type AnnouncementBottomButtonProps = {
  action: AnnouncementAction;
  onAction: (action: AnnouncementAction) => void;
};

const AnnouncementBottomButton = memo(({ action, onAction }: AnnouncementBottomButtonProps) => {
  const handleClick = useAnnouncementActionClick(action, onAction);

  if (isAnnouncementCommandAction(action)) {
    return (
      <Button
        appearance="filled"
        color="blue"
        data-action-label={action.label}
        data-testid="ANNOUNCEMENT_ACTION"
        onClick={handleClick}
        size="small"
      >
        {action.label}
      </Button>
    );
  }

  return (
    <Button appearance="filled" asChild color="blue" onClick={handleClick} size="small">
      <a
        data-action-label={action.label}
        data-testid="ANNOUNCEMENT_ACTION"
        href={action.url}
        {...getAnnouncementAnchorTargetProps(action.url)}
      >
        {action.label}
      </a>
    </Button>
  );
});

AnnouncementBottomButton.displayName = 'AnnouncementBottomButton';

export type AnnouncementBottomButtonsProps = {
  buttons: readonly AnnouncementAction[];
  onAction: (action: AnnouncementAction) => void;
};

export const AnnouncementBottomButtons = memo(({ buttons, onAction }: AnnouncementBottomButtonsProps) => {
  if (buttons.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {buttons.map((action, index) => (
        <AnnouncementBottomButton action={action} key={`${action.label}-${index}`} onAction={onAction} />
      ))}
    </div>
  );
});

AnnouncementBottomButtons.displayName = 'AnnouncementBottomButtons';
